import * as React from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { getHelpUrl } from "@repo/utils";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarItem,
  SidebarSeparator,
  Badge,
  Button,
} from "@repo/ui";
import {
  House,
  Package,
  ArrowLineDown,
  ArrowLineUp,
  Pulse,
  Bell,
  Warning,
  UserCircle,
  FileMagnifyingGlass,
  ChartBar,
  Scroll,
  Users,
  Gear,
  Cube,
  ArrowsLeftRight,
  Question,
} from "@phosphor-icons/react";
import { usePermissionStore } from "@/lib/stores/permission-store";
import type { Permission, Role } from "@/lib/permissions";

function RouterLink({
  href,
  ...props
}: { href: string } & Record<string, unknown>) {
  return <Link to={href} {...props} />;
}

interface AppSidebarProps {
  alertCount?: number;
  reminderDueCount?: number;
  userRole?: Role;
  onSwitchUser?: () => void;
}

export function AppSidebar({
  alertCount = 0,
  reminderDueCount = 0,
  userRole,
  onSwitchUser,
}: AppSidebarProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [helpUrl, setHelpUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    setHelpUrl(getHelpUrl(window.location.hostname));
  }, []);

  const isActive = (path: string) => pathname === path;

  const check = usePermissionStore((s) => s.check);
  // Subscribe to overrides so the sidebar re-renders when overrides are loaded/updated
  const _overrides = usePermissionStore((s) => s.overrides);

  /** Returns true if the user's role is not denied for the given permission (respects overrides). */
  const canAccess = (permission: Permission) => {
    if (!userRole) return false;
    const result = check(permission);
    return result !== null && result.type !== "denied";
  };

  return (
    <Sidebar defaultCollapsed={false} collapsible linkComponent={RouterLink}>
      <SidebarContent>
        {/* Main Navigation */}
        <SidebarGroup label="Main">
          <SidebarItem
            href="/dashboard"
            icon={
              <House
                weight={isActive("/dashboard") ? "fill" : "regular"}
                size={18}
              />
            }
            active={isActive("/dashboard")}
            data-tour="nav-dashboard"
          >
            Dashboard
          </SidebarItem>
          {canAccess("inventory:view") && (
            <SidebarItem
              href="/inventory"
              icon={
                <Package
                  weight={isActive("/inventory") ? "fill" : "regular"}
                  size={18}
                />
              }
              active={isActive("/inventory")}
              data-tour="nav-inventory"
              data-testid="nav-inventory"
            >
              Inventory
            </SidebarItem>
          )}
          {canAccess("dispenses:view") && (
            <SidebarItem
              href="/dispenses"
              icon={
                <ArrowLineUp
                  weight={isActive("/dispenses") ? "bold" : "regular"}
                  size={18}
                />
              }
              active={isActive("/dispenses")}
              data-tour="nav-dispenses"
              data-testid="nav-dispenses"
            >
              Dispenses
            </SidebarItem>
          )}
          {canAccess("receipts:view") && (
            <SidebarItem
              href="/receipts"
              icon={
                <ArrowLineDown
                  weight={isActive("/receipts") ? "bold" : "regular"}
                  size={18}
                />
              }
              active={isActive("/receipts")}
              data-tour="nav-receipts"
            >
              Receipts
            </SidebarItem>
          )}
          {canAccess("activity:view") && (
            <SidebarItem
              href="/activity"
              icon={
                <Pulse
                  weight={isActive("/activity") ? "fill" : "regular"}
                  size={18}
                />
              }
              active={isActive("/activity")}
            >
              Activity
            </SidebarItem>
          )}
          {canAccess("reminders:view") && (
            <SidebarItem
              href="/reminders"
              icon={
                <Bell
                  weight={isActive("/reminders") ? "fill" : "regular"}
                  size={18}
                />
              }
              active={isActive("/reminders")}
              badge={
                reminderDueCount > 0 ? (
                  <Badge
                    variant="warning"
                    className="h-5 min-w-5 justify-center px-1.5 text-[10px] font-semibold"
                  >
                    {reminderDueCount}
                  </Badge>
                ) : undefined
              }
            >
              Reminders
            </SidebarItem>
          )}
          {canAccess("alerts:view") && (
            <SidebarItem
              href="/alerts"
              icon={
                <Warning
                  weight={isActive("/alerts") ? "fill" : "regular"}
                  size={18}
                />
              }
              active={isActive("/alerts")}
              badge={
                alertCount > 0 ? (
                  <Badge
                    variant="destructive"
                    className="h-5 min-w-5 justify-center px-1.5 text-[10px] font-semibold"
                  >
                    {alertCount}
                  </Badge>
                ) : undefined
              }
            >
              Alerts
            </SidebarItem>
          )}
          {canAccess("patients:view") && (
            <SidebarItem
              href="/patients"
              icon={
                <UserCircle
                  weight={isActive("/patients") ? "fill" : "regular"}
                  size={18}
                />
              }
              active={isActive("/patients")}
            >
              Patients
            </SidebarItem>
          )}
        </SidebarGroup>

        <SidebarSeparator />

        {/* Reports & Analytics */}
        <SidebarGroup label="Insights">
          {canAccess("reports:view") && (
            <SidebarItem
              href="/reports"
              icon={
                <FileMagnifyingGlass
                  weight={isActive("/reports") ? "fill" : "regular"}
                  size={18}
                />
              }
              active={isActive("/reports")}
              data-tour="nav-reports"
            >
              Reports
            </SidebarItem>
          )}
          {canAccess("analytics:view") && (
            <SidebarItem
              href="/analytics"
              icon={
                <ChartBar
                  weight={isActive("/analytics") ? "fill" : "regular"}
                  size={18}
                />
              }
              active={isActive("/analytics")}
              data-testid="nav-analytics"
            >
              Analytics
            </SidebarItem>
          )}
          {canAccess("audit_log:view") && (
            <SidebarItem
              href="/audit"
              icon={
                <Scroll
                  weight={isActive("/audit") ? "fill" : "regular"}
                  size={18}
                />
              }
              active={isActive("/audit")}
              data-tour="nav-audit"
              data-testid="nav-audit"
            >
              Audit Log
            </SidebarItem>
          )}
        </SidebarGroup>

        <SidebarSeparator />

        {/* System */}
        <SidebarGroup label="System">
          {canAccess("users:view") && (
            <SidebarItem
              href="/users"
              icon={
                <Users
                  weight={isActive("/users") ? "fill" : "regular"}
                  size={18}
                />
              }
              active={isActive("/users")}
              data-testid="nav-users"
            >
              Users
            </SidebarItem>
          )}
          {canAccess("settings:pharmacy") && (
            <SidebarItem
              href="/settings"
              icon={
                <Gear
                  weight={isActive("/settings") ? "fill" : "regular"}
                  size={18}
                />
              }
              active={isActive("/settings")}
            >
              Settings
            </SidebarItem>
          )}
          {import.meta.env.MODE === "development" && (
            <SidebarItem
              href="/components"
              icon={
                <Cube
                  weight={isActive("/components") ? "fill" : "regular"}
                  size={18}
                />
              }
              active={isActive("/components")}
            >
              Components
            </SidebarItem>
          )}
          <a
            href={helpUrl ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors duration-150 hover:bg-muted/60 hover:text-foreground"
          >
            <Question weight="regular" size={18} className="shrink-0" />
            <span className="flex-1 text-left">Help Center</span>
          </a>
        </SidebarGroup>
      </SidebarContent>

      {onSwitchUser && (
        <SidebarFooter>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2"
            onClick={onSwitchUser}
          >
            <ArrowsLeftRight className="h-4 w-4" />
            Switch user
          </Button>
        </SidebarFooter>
      )}
    </Sidebar>
  );
}
